/**
 * scene-export.ts — assembles a viewer-ready scene bundle.
 *
 * Output: scene.json (built by scene-doc.ts, schemaVersion 1) plus one asset
 * per element: a splat as a streamed LOD bundle (`<id>/lod-meta.json` and its
 * chunks), or as a single `.sog`, or as a raw `.ply` when nothing better could
 * be built; a model as its original `.glb`. Optional thumbnail.jpg and
 * preview.mp4 ride along.
 *
 * The same bundle feeds three destinations: a .zip download, the viewer dev
 * server ("Preview in viewer", content-addressed so only changed files ship),
 * and the backend save (backend.ts, which size-diffs against the remote
 * manifest). The editor's native .ssproj stays untouched — that is the
 * round-trip format; this drops editor-only state for viewer fidelity.
 */

import { MemoryFileSystem, ZipFileSystem, type ReadFileSystem } from '@playcanvas/splat-transform';

import { ElementType } from '../element';
import type { Events } from '../events';
import { BrowserFileSystem } from '../io';
import type { Scene } from '../scene';
import { writeSplatFile } from '../splat-serialize';
import type { Splat } from '../splat';
import { i18n } from '../ui/localization';
import { buildLodViaHelper, lodHelperAvailable } from './lod-helper-client';
import type { Model } from './model';
import type { VoxelFiles } from './walk-voxels';
import { buildSceneJson, markerToJson, slugify } from './scene-doc';

// LOD / SOG / PLY all stay in local space — scene.json carries the transform
const SERIALIZE_SETTINGS = { keepWorldTransform: true };

// beyond this the browser builder is likely to hit the WebGPU buffer cap
const BROWSER_LOD_LIMIT = 800_000;
const LOD_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Derived export data per splat, kept off the Splat itself so upstream's
 * class stays untouched. Invalidated by any geometry edit (see below).
 */
type ExportCache = {
    lodAssets?: Map<string, Uint8Array>;
    sogAsset?: Uint8Array;
    /** the backend already holds this splat's LOD under this dir (metadata-only save) */
    lodRemoteDir?: string | null;
    /** LOD failed once this session; don't retry every save */
    lodSkipped?: boolean;
    /** the premade LOD bundle this splat was opened from; ships as is until a geometry edit */
    lodBundle?: ReadFileSystem;
};

/**
 * Every file of a premade LOD bundle: lod-meta.json, then each file a JSON
 * names (chunk meta.json, its .webp textures), relative to that JSON's folder.
 */
const readLodBundle = async (fs: ReadFileSystem): Promise<Map<string, Uint8Array>> => {
    const files = new Map<string, Uint8Array>();
    const queue = ['lod-meta.json'];
    const refs = (v: unknown, out: string[]) => {
        if (typeof v === 'string') {
            if (/^[\w./-]+\.(json|webp)$/i.test(v) && !v.includes('..')) out.push(v);
        } else if (Array.isArray(v)) {
            v.forEach(x => refs(x, out));
        } else if (v && typeof v === 'object') {
            Object.values(v).forEach(x => refs(x, out));
        }
    };
    while (queue.length) {
        const name = queue.shift()!;
        if (files.has(name)) continue;
        const source = await fs.createSource(name);
        let bytes: Uint8Array;
        try {
            bytes = await source.read().readAll();
        } finally {
            source.close();
        }
        files.set(name, bytes);
        if (name.endsWith('.json')) {
            const dir = name.slice(0, name.lastIndexOf('/') + 1);
            const found: string[] = [];
            refs(JSON.parse(new TextDecoder().decode(bytes)), found);
            for (const f of found) queue.push(dir + f);
        }
    }
    return files;
};

const fetchModelBytes = async (model: Model): Promise<ArrayBuffer | null> => {
    const file: any = (model.asset as any)?.file;
    if (!file) return null;
    if (file.contents) {
        const c = file.contents;
        if (c instanceof ArrayBuffer) return c;
        if (c instanceof Uint8Array) {
            const copy = new Uint8Array(c.byteLength);
            copy.set(c);
            return copy.buffer;
        }
        if (typeof Blob !== 'undefined' && c instanceof Blob) return await c.arrayBuffer();
    }
    if (file.url) {
        try {
            const r = await fetch(file.url);
            if (r.ok) return await r.arrayBuffer();
            console.warn(`[scene-export] model GLB fetch ${file.url} → HTTP ${r.status}`);
        } catch (err) {
            console.warn('[scene-export] model GLB fetch threw:', err);
        }
    }
    return null;
};

const uniqueName = (base: string, ext: string, used: Set<string>): string => {
    let name = `${base}.${ext}`;
    let n = 1;
    while (used.has(name)) name = `${base}-${n++}.${ext}`;
    used.add(name);
    return name;
};

const withTimeout = <T>(p: Promise<T>, ms: number, what: string): Promise<T> => {
    return Promise.race([
        p,
        new Promise<T>((_, reject) => {
            window.setTimeout(() => reject(new Error(`LOD build timed out after ${ms / 1000}s (${what})`)), ms);
        })
    ]);
};

export type CollectBundleOptions = {
    /** splat slugs whose LOD the backend already holds — emit no chunks for them */
    remoteLod?: string[];
    /** media the backend already holds, so scene.json keeps referencing it */
    remoteHas?: { thumbnail?: boolean; preview?: boolean };
};

export type Bundle = {
    entries: Map<string, Uint8Array>;
    splats: number;
    models: number;
};

const registerSceneExportEvents = (scene: Scene, events: Events) => {
    const cache = new WeakMap<Splat, ExportCache>();
    const cacheFor = (s: Splat): ExportCache => {
        let c = cache.get(s);
        if (!c) {
            c = {};
            cache.set(s, c);
        }
        return c;
    };

    // Ops that leave the gaussians untouched (whole-entity transform, pivot,
    // selection) keep the built LOD valid; anything else must rebuild it.
    const GEOMETRY_CLEAN = new Set(['entityTransform', 'setPivot', 'setLocalFrame', 'selectAll', 'selectNone', 'selectInvert', 'selectOp', 'splatRename']);
    const isGeometryClean = (op: any): boolean => {
        if (!op || typeof op.name !== 'string') return false;
        if (op.name === 'multiOp' && Array.isArray(op.ops)) return op.ops.length > 0 && op.ops.every(isGeometryClean);
        return GEOMETRY_CLEAN.has(op.name);
    };
    events.on('edit.add', (op: any) => {
        if (isGeometryClean(op)) return;
        for (const s of events.invoke('scene.allSplats') as Splat[]) {
            const c = cache.get(s);
            if (c) {
                delete c.lodAssets;
                delete c.sogAsset;
                delete c.lodSkipped;
                delete c.lodBundle;
            }
        }
    });
    // opened from a premade LOD bundle (file-handler.ts): reuse it on save
    events.on('splat.lodBundle', (s: Splat, fs: ReadFileSystem) => {
        cacheFor(s).lodBundle = fs;
    });
    // every splat ships as a LOD (built, premade or already on the backend):
    // the stored scene files reopen it, so a save needs no .ssproj (backend.ts)
    events.function('scene.lodSourceOnly', () => {
        const splats = scene.getElementsByType(ElementType.splat) as Splat[];
        return splats.length > 0 && splats.every((s) => {
            const c = cache.get(s);
            return !!(c && (c.lodAssets || c.lodRemoteDir || c.lodBundle));
        });
    });

    const splatSlug = (s: Splat, i: number) => slugify(s.name) || `splat-${i}`;

    const status = (text: string) => events.fire('spinner.text', text);

    // Build LOD (helper → browser) or SOG for every splat that has neither,
    // in place. Throws SaveCancelled when the user declines the slow path.
    const ensureSplatAssets = async (splats: Splat[]) => {
        // premade LOD bundles ship as they are, no rebuild
        for (const s of splats) {
            const c = cacheFor(s);
            if (c.lodAssets || c.lodRemoteDir || !c.lodBundle) continue;
            try {
                status(`Reading LOD bundle (${s.name})…`);
                c.lodAssets = await readLodBundle(c.lodBundle);
                console.log(`[scene-export] LOD ${s.name}: premade bundle, ${c.lodAssets.size} files`);
            } catch (err) {
                delete c.lodBundle;
                console.error(`[scene-export] premade LOD bundle for ${s.name} unreadable — building instead:`, err);
            }
        }

        const helper = await lodHelperAvailable();
        const hasWebGPU = !!navigator.gpu;
        const needing = splats.filter((s) => {
            const c = cacheFor(s);
            return !c.lodAssets && !c.lodRemoteDir && !c.sogAsset && (helper || !c.lodSkipped);
        });
        if (needing.length === 0) return;

        const { buildLodAssets, buildSogAsset } = await import('./lod-build');

        if (!helper) {
            const big = needing.filter(s => s.numSplats > BROWSER_LOD_LIMIT);
            if (big.length > 0) {
                events.fire('stopSpinner');
                const result = await events.invoke('showPopup', {
                    type: 'yesno',
                    header: i18n.t('ollo.export.helper-offline-header'),
                    message: i18n.t('ollo.export.helper-offline', { count: big.length })
                });
                events.fire('startSpinner');
                if (result?.action !== 'yes') {
                    const err = new Error(i18n.t('ollo.export.cancelled-helper'));
                    err.name = 'SaveCancelled';
                    throw err;
                }
            }
        }

        let built = 0;
        for (let i = 0; i < needing.length; i++) {
            const s = needing[i];
            const c = cacheFor(s);
            const tag = `${i + 1}/${needing.length} (${s.name})`;
            try {
                if (helper) {
                    try {
                        c.lodAssets = await buildLodViaHelper(s, {
                            onProgress: (_f, label) => status(`LOD ${tag} (local GPU) — ${label}`)
                        });
                    } catch (helperErr) {
                        if (!hasWebGPU) throw helperErr;
                        console.error(`[scene-export] helper LOD ${tag} failed — building in browser:`, helperErr);
                        c.lodAssets = (await withTimeout(buildLodAssets(s, {
                            onProgress: (f, label) => status(`LOD ${tag} (browser) — ${(f * 100).toFixed(0)}% ${label}`)
                        }), LOD_TIMEOUT_MS, tag)).files;
                    }
                } else if (hasWebGPU) {
                    c.lodAssets = (await withTimeout(buildLodAssets(s, {
                        onProgress: (f, label) => status(`LOD ${tag} — ${(f * 100).toFixed(0)}% ${label}`)
                    }), LOD_TIMEOUT_MS, tag)).files;
                } else {
                    throw new Error('no local LOD helper and no WebGPU');
                }
                built++;
                console.log(`[scene-export] LOD ${tag}: ${c.lodAssets.size} files`);
            } catch (err) {
                c.lodSkipped = true;
                console.error(`[scene-export] LOD ${tag} failed — shipping SOG instead:`, err);
                if (hasWebGPU) {
                    try {
                        c.sogAsset = await buildSogAsset(s, {
                            onProgress: (f, label) => status(`SOG ${tag} — ${(f * 100).toFixed(0)}% ${label}`)
                        });
                    } catch (sogErr) {
                        console.warn(`[scene-export] SOG ${tag} also failed — shipping raw PLY:`, sogErr);
                    }
                }
            }
        }
        if (built > 0) events.fire('lod.built');
    };

    const collectBundleEntries = async (opts?: CollectBundleOptions): Promise<Bundle> => {
        const entries = new Map<string, Uint8Array>();
        const splats = scene.getElementsByType(ElementType.splat) as Splat[];
        const models = scene.getElementsByType(ElementType.model) as Model[];

        // metadata-only save: the backend already holds these LOD dirs
        const remoteLod = new Set(opts?.remoteLod ?? []);
        splats.forEach((s, i) => {
            const c = cacheFor(s);
            c.lodRemoteDir = (!c.lodAssets && remoteLod.has(splatSlug(s, i))) ? splatSlug(s, i) : null;
        });

        events.fire('startSpinner');
        try {
            await ensureSplatAssets(splats);
        } catch (err) {
            if ((err as Error)?.name === 'SaveCancelled') throw err;
            console.warn('[scene-export] asset build step errored — shipping PLY for unbuilt splats:', err);
        } finally {
            events.fire('stopSpinner');
        }

        // ── file names ──────────────────────────────────────────────────
        const used = new Set<string>(['scene.json']);
        const splatUrls = new Map<number, string>();
        splats.forEach((s, i) => {
            const c = cacheFor(s);
            const base = splatSlug(s, i);
            if (c.lodAssets) {
                let dir = base;
                let n = 1;
                while (used.has(`${dir}/`)) dir = `${base}-${n++}`;
                used.add(`${dir}/`);
                splatUrls.set(s.uid, `${dir}/lod-meta.json`);
            } else if (c.lodRemoteDir) {
                used.add(`${c.lodRemoteDir}/`);
                splatUrls.set(s.uid, `${c.lodRemoteDir}/lod-meta.json`);
            } else if (c.sogAsset) {
                splatUrls.set(s.uid, uniqueName(base, 'sog', used));
            } else {
                splatUrls.set(s.uid, uniqueName(base, 'ply', used));
            }
        });
        const modelUrls = new Map<number, string>();
        models.forEach((m, i) => modelUrls.set(m.uid, uniqueName(slugify(m.name) || `model-${i}`, 'glb', used)));

        // ── media ───────────────────────────────────────────────────────
        const thumbnailBytes = events.invoke('thumbnail.getBytes') as Uint8Array | null;
        if (thumbnailBytes) entries.set('thumbnail.jpg', thumbnailBytes);
        const previewVideoBytes = events.invoke('preview.getBytes') as Uint8Array | null;
        if (previewVideoBytes) entries.set('preview.mp4', previewVideoBytes);

        // ── scene.json ──────────────────────────────────────────────────
        const viewerSettings = events.invoke('viewer.settings') as
            { navigation?: 'free' | 'rotating'; orbit?: object } | null;
        let editorRadius = 1;
        for (const s of splats) {
            const wb = s.worldBound;
            if (wb) editorRadius = Math.max(editorRadius, wb.halfExtents.length());
        }
        const docJson = buildSceneJson(scene, events, {
            splatUrls,
            modelUrls,
            markers: ((events.invoke('markers.serialize') as unknown[] | null) ?? []).map(markerToJson),
            navigation: viewerSettings?.navigation,
            orbit: viewerSettings?.navigation === 'rotating' ?
                { ...viewerSettings?.orbit, sceneRadius: +editorRadius.toFixed(3) } :
                undefined,
            thumbnailUrl: (thumbnailBytes || opts?.remoteHas?.thumbnail) ? 'thumbnail.jpg' : undefined,
            previewVideoUrl: (previewVideoBytes || opts?.remoteHas?.preview) ? 'preview.mp4' : undefined
        });
        // collision voxels ship beside the scene; scene.json points at them
        const voxelFiles = events.functions.has('walk.voxels.files') ?
            events.invoke('walk.voxels.files') as VoxelFiles | null : null;
        if (voxelFiles && docJson.walk?.voxels) {
            const base = `collision/${slugify(voxelFiles.name) || 'collision'}`;
            entries.set(`${base}.voxel.json`, voxelFiles.json);
            entries.set(`${base}.voxel.bin`, voxelFiles.bin);
            docJson.walk.voxels.url = `${base}.voxel.json`;
        }
        entries.set('scene.json', new TextEncoder().encode(JSON.stringify(docJson, null, 2)));

        // ── splat assets ────────────────────────────────────────────────
        for (const s of splats) {
            const c = cacheFor(s);
            const url = splatUrls.get(s.uid)!;
            if (c.lodRemoteDir) continue;
            if (c.lodAssets) {
                const dir = url.slice(0, url.lastIndexOf('/'));
                for (const [name, bytes] of c.lodAssets.entries()) entries.set(`${dir}/${name}`, bytes);
            } else if (c.sogAsset) {
                entries.set(url, c.sogAsset);
            } else {
                const tmp = new MemoryFileSystem();
                await writeSplatFile([s], SERIALIZE_SETTINGS, 'ply', url, {}, tmp);
                const bytes = tmp.results.get(url);
                if (bytes) entries.set(url, bytes);
            }
        }

        // ── models ──────────────────────────────────────────────────────
        await Promise.all(models.map(async (m) => {
            const bytes = await fetchModelBytes(m);
            if (bytes) {
                entries.set(modelUrls.get(m.uid)!, new Uint8Array(bytes));
            } else {
                console.warn(`[scene-export] model "${m.name}" could not be fetched — skipped (scene.json still references it)`);
            }
        }));

        return { entries, splats: splats.length, models: models.length };
    };

    const writeEntriesToZip = async (entries: Map<string, Uint8Array>, zipFs: ZipFileSystem) => {
        const ordered = ['scene.json', ...[...entries.keys()].filter(k => k !== 'scene.json')];
        for (const path of ordered) {
            const bytes = entries.get(path);
            if (!bytes) continue;
            const w = await zipFs.createWriter(path);
            await w.write(bytes);
            await w.close();
        }
    };

    const sha256hex = async (bytes: Uint8Array): Promise<string> => {
        const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
        return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
    };

    /** Download path: the bundle as a .zip. */
    const exportBundle = async (filename: string, stream?: FileSystemWritableFileStream) => {
        events.fire('startSpinner');
        try {
            const { entries, splats, models } = await collectBundleEntries();
            const browserFs = new BrowserFileSystem(filename, stream);
            const browserWriter = await browserFs.createWriter(filename);
            const zipFs = new ZipFileSystem(browserWriter);
            await writeEntriesToZip(entries, zipFs);
            await zipFs.close();
            events.fire('scene.exported', { filename, splats, models });
        } catch (err) {
            if ((err as Error)?.name === 'SaveCancelled') return;
            console.error('[scene-export]', err);
            await events.invoke('showPopup', {
                type: 'error',
                header: i18n.t('ollo.export.failed'),
                message: `${(err as any)?.message ?? err}`
            });
        } finally {
            events.fire('stopSpinner');
        }
    };

    /**
     * Preview path: POST the bundle to the viewer dev server
     * (vite-plugin-publish-scene), content-addressed: only files whose SHA-256
     * differs from the server's manifest ship, and a `__manifest.json` of the
     * complete desired set lets the server prune deleted files.
     */
    const publishToViewer = async (
        { name, origin }: { name: string; origin: string }
    ): Promise<{ ok: boolean; files?: number; total?: number; assetsChanged?: boolean; error?: string }> => {
        events.fire('startSpinner');
        try {
            const { entries } = await collectBundleEntries();
            const base = origin.replace(/\/+$/, '');
            const desired: Record<string, string> = {};
            for (const [path, bytes] of entries) desired[path] = await sha256hex(bytes);
            let remote: Record<string, string> = {};
            try {
                const m = await fetch(`${base}/__publish-scene/manifest?name=${encodeURIComponent(name)}`);
                if (m.ok) remote = ((await m.json()) as { files?: Record<string, string> }).files ?? {};
            } catch { /* no manifest endpoint → ship everything */ }
            const changed = [...entries.keys()].filter(p => remote[p] !== desired[p]);

            const memFs = new MemoryFileSystem();
            const writer = memFs.createWriter('bundle.zip');
            const zipFs = new ZipFileSystem(writer);
            const mw = await zipFs.createWriter('__manifest.json');
            await mw.write(new TextEncoder().encode(JSON.stringify({ files: desired })));
            await mw.close();
            for (const path of changed) {
                const w = await zipFs.createWriter(path);
                await w.write(entries.get(path)!);
                await w.close();
            }
            await zipFs.close();
            const zipBytes = memFs.results.get('bundle.zip');
            if (!zipBytes) throw new Error('bundle assembly produced no bytes');

            const resp = await fetch(`${base}/__publish-scene?name=${encodeURIComponent(name)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/zip' },
                body: zipBytes as BodyInit
            });
            if (!resp.ok) {
                const text = await resp.text().catch(() => '');
                throw new Error(`viewer rejected publish (HTTP ${resp.status}) ${text}`.trim());
            }
            const json = await resp.json().catch(() => ({})) as { written?: number; total?: number };
            return {
                ok: true,
                files: json.written ?? changed.length,
                total: json.total ?? entries.size,
                assetsChanged: changed.some(p => p !== 'scene.json')
            };
        } catch (err) {
            if ((err as Error)?.name !== 'SaveCancelled') console.error('[scene-export] publishToViewer:', err);
            return { ok: false, error: (err as any)?.message ?? String(err) };
        } finally {
            events.fire('stopSpinner');
        }
    };

    // File → Build LOD assets: build now so the next save or export is instant
    events.function('lod.buildAll', async () => {
        const splats = events.invoke('scene.allSplats') as Splat[];
        if (!splats?.length) return;
        events.fire('startSpinner');
        try {
            await ensureSplatAssets(splats);
        } catch (err: any) {
            if (err?.name !== 'SaveCancelled') {
                console.error('[scene-export] build LOD failed', err);
                await events.invoke('showPopup', { type: 'error', header: i18n.t('ollo.export.failed'), message: String(err?.message ?? err) });
            }
        } finally {
            events.fire('stopSpinner');
        }
    });

    events.function('scene.publishToViewer', (arg: { name: string; origin: string }) => publishToViewer(arg));
    events.function('scene.collectBundle', (opts?: CollectBundleOptions) => collectBundleEntries(opts));
    events.function('scene.exportBundle', async () => {
        if (events.invoke('scene.empty') && scene.getElementsByType(ElementType.model).length === 0) {
            await events.invoke('showPopup', {
                type: 'error',
                header: i18n.t('ollo.export.empty-header'),
                message: i18n.t('ollo.export.empty')
            });
            return;
        }
        const docName = (events.invoke('doc.name') as string) ?? 'scene';
        const suggested = `${slugify(docName.replace(/\.[^.]+$/, '')) || 'scene'}.zip`;
        if (window.showSaveFilePicker) {
            try {
                const handle = await window.showSaveFilePicker({
                    id: 'OlloportSceneBundle',
                    suggestedName: suggested,
                    types: [{
                        description: 'Olloport scene bundle (zip)',
                        accept: { 'application/zip': ['.zip'] }
                    }] as any
                });
                await exportBundle(handle.name, await handle.createWritable());
            } catch (err) {
                if ((err as any).name !== 'AbortError') console.error('[scene-export] save picker:', err);
            }
        } else {
            await exportBundle(suggested);
        }
    });
};

export { registerSceneExportEvents };
